import { ApiEndpoints } from '@/config/index';
import { OnlineStatusModel } from '@/models/OnlineStatus';
import { OnlineStatusEnums } from '@/enums/OnlineStatusEnums';
import { LiteEvent } from '@/models/event/liteEvent';
import { LoggerService } from '../utils/index';
import { AxiosService } from '.';

class OnlineProvider {
    private refreshTime = 1 * 60 * 1000; // 1min in millisec
    private lastRefresh?: number;
    private onlineStatus = OnlineStatusEnums.UNKNOWN;
    private oldOnlineStatus = OnlineStatusEnums.UNKNOWN;

    private readonly onOnlineStausChanged = new LiteEvent<{ old: OnlineStatusEnums, new: OnlineStatusEnums }>();

    constructor() {
        // listen for network status changes
        window.addEventListener('online', () => {
            LoggerService.LogDebug(`online status changed to online`);
            this.FetchIsOnline();
        });
        window.addEventListener('offline', () => {
            LoggerService.LogDebug(`online status changed to offline`);
            this.FetchIsOnline();
        });
    }

    /**
     * Method to check if we have a network connection and can reach the api server
     */
    public async FetchIsOnline(): Promise<OnlineStatusModel> {
        const self = this;
        return new Promise(async (resolve, reject) => {
            this.resolveOnlineStatus()
                .then((data) => {
                    resolve(data);
                })
                .catch((error) => {
                    reject(new OnlineStatusModel({ status: this.onlineStatus, message: error }));
                })
                .finally(() => {
                    this.onOnlineStausChanged.trigger({
                        old: this.oldOnlineStatus,
                        new: this.onlineStatus,
                    });
                });
        });
    }

    /**
     * Event getter for online status
     */
    public get OnOnlineStausChanged() {
        return this.onOnlineStausChanged.expose();
    }

    public get GetOnlineStatus() {
        return this.onlineStatus;
    }

    private resolveOnlineStatus(): Promise<OnlineStatusModel> {
        const self = this;
        return new Promise(async (resolve, reject) => {
            // check for network connection
            if (navigator.onLine) {
                const now = (new Date()).getTime();
                // if last refresh is to old, re-check if a connection to server is available
                if (self.lastRefresh === undefined || now - self.lastRefresh >= self.refreshTime) {
                    // TODO: define a ping API endpoint
                    await AxiosService.get(ApiEndpoints.onlinePing, null)
                        .then((data) => {
                            self.lastRefresh = now;
                            this.setOnlineStatus(OnlineStatusEnums.ONLINE);
                            resolve(new OnlineStatusModel({ status: OnlineStatusEnums.ONLINE }));
                        })
                        .catch((error) => {
                            self.lastRefresh = now;
                            this.setOnlineStatus(OnlineStatusEnums.API_ERROR);
                            // since we handle online status our way we use resolve here
                            resolve(new OnlineStatusModel({ status: OnlineStatusEnums.API_ERROR, message: error }));
                        });
                } else {
                    // we have network and there was a connection to the api server at least refreshTime ago
                    this.setOnlineStatus(OnlineStatusEnums.ONLINE);
                    resolve(new OnlineStatusModel({ status: OnlineStatusEnums.ONLINE }));
                }
            } else {
                // we have no network connection
                this.setOnlineStatus(OnlineStatusEnums.NO_NETWORK);
                resolve(new OnlineStatusModel({ status: OnlineStatusEnums.NO_NETWORK }));
            }
        });
    }

    private setOnlineStatus(inStatus: OnlineStatusEnums) {
        this.oldOnlineStatus = this.onlineStatus;
        this.onlineStatus = inStatus;
    }
}

export default OnlineProvider;
